import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { injectIntl, intlShape } from "@edx/frontend-platform/i18n";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";

import { getDashboardPlatformURL } from "../../utils/utility";
import {
  faBookmark,
  faArrowRight,
  faCaretRight,
} from "@fortawesome/free-solid-svg-icons";
import { fetchBookmarkTabData } from "../data/thunks";
import messages from "../outline-tab/messages";
import PageLoading from "../../generic/PageLoading";
import { default as bookmarkMessages } from './messages';
import { getLocalizedFomattedDate, getLocalizedFormattedNumber } from "../../utils/i18n";
import { default as ReactPagination } from "../../components/ReactPaginate";
import TextBlockSkeleton from "../../components/Skeletons/TextBlockSkeleton";

const NO_OF_ROWS_PER_PAGE = 6;

const RenderBookmarkList = injectIntl(({ intl, bookmarkData }) => {
  const {
    displayName: unitName,
    courseId,
    path,
    usageId: unitId,
    created,
  } = bookmarkData;
  const { displayName: sectionName, usageKey: sectionId } = path[0];
  const { displayName: sequenceName, usageKey: sequenceId } = path[1];

  const { locale = 'en' } = useSelector((state) => state.app);

  return (
    <Link
      to={`/course/${courseId}/${sectionId}/${sequenceId}/${unitId}`}
      className="bookmark-list-item"
    >
      <div className="list-item-content">
        <FontAwesomeIcon icon={faBookmark} />
        <h3 className="list-item-breadcrumbtrail">
          {sectionName}
          <FontAwesomeIcon icon={faCaretRight} />
          {sequenceName} <FontAwesomeIcon icon={faCaretRight} />
          {unitName}
        </h3>
        <p className="list-item-date">
          {intl.formatMessage(bookmarkMessages.bookmarkDate, { date: getLocalizedFomattedDate(created, locale) })}
        </p>
        <p className="list-item-view-btn text-end">
          <span aria-hidden="true">{intl.formatMessage(bookmarkMessages.bookmarkView)}</span>
          <FontAwesomeIcon icon={faArrowRight} />
        </p>
      </div>
    </Link>
  );
});

function BookmarkTab({ intl }) {
  const dispatch = useDispatch();
  const { courseId: courseIdFromUrl } = useParams();
  const { status, data } = useSelector((state) => state.courseHome.bookmark);

  const { count, currentPage, numPages, results } = data || {};
  const { locale = 'en' } = useSelector((state) => state.app);

  const getResultsRange = () => {
    if (!count) {
      return '';
    }

    const startCount = (currentPage - 1) * NO_OF_ROWS_PER_PAGE + 1;
    const totalResultsCount = currentPage * NO_OF_ROWS_PER_PAGE;
    const endCount = totalResultsCount <= count ? totalResultsCount : count;

    if (currentPage === 1) {
      return `${getLocalizedFormattedNumber(currentPage, locale)}-${getLocalizedFormattedNumber(endCount, locale)}`;
    }

    return `${getLocalizedFormattedNumber(startCount, locale)}-${getLocalizedFormattedNumber(endCount, locale)}`;
  };

  React.useEffect(() => {
    dispatch(fetchBookmarkTabData(courseIdFromUrl));
  }, []);

  return (
    <div className="container">
      <div className="row">
        <div className="main-content-wrapper w-100">
          <div className="position-relative breadcrumbs-wrapper pb-2">
            <nav aria-label="breadcrumb" className="mt-4 d-inline-block">
              <ol className="list-unstyled d-flex flex-wrap flex-lg-nowrap align-items-center m-0">
                <li>
                  <Link
                    className="flex-shrink-0"
                    to={`#`}
                    onClick={() =>
                      window.location.assign(getDashboardPlatformURL())
                    }
                  >
                    {intl.formatMessage(bookmarkMessages.homeBreadCrumb)}
                  </Link>
                </li>
                <li>
                  <span>/</span>
                </li>
                <li>
                  <a className="flex-shrink-0 color-dark-grey" href="#">
                    {intl.formatMessage(bookmarkMessages.bookmarkBreadCrumb)}
                  </a>
                </li>
              </ol>
            </nav>
          </div>

          <div className="bookmarked-wrapper">
            <h2>{intl.formatMessage(bookmarkMessages.bookmarkHeading)}</h2>
            {status === "loading" ? (
              [1, 2].map((i) => (
                <TextBlockSkeleton 
                  key={i} 
                  srMessage={i === 1 ? intl.formatMessage(messages.loading) : ''} 
                />
              ))
            ) : !count ? (
              <div className="bookmark-box bookmarks-empty my-4">
                <h3 className="bookmarks-empty-header">
                  <FontAwesomeIcon icon={faBookmark} />
                  {intl.formatMessage(bookmarkMessages.noBookmarksHeading)}
                </h3>
                <div className="bookmarks-empty-detail">
                  <span className="bookmarks-empty-detail-title">
                    {intl.formatMessage(bookmarkMessages.noBookmarksText)}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="bookmark-list my-4">
                  <div className="text-end mb-3">
                    <h4>{intl.formatMessage(bookmarkMessages.bookmarkTotal, { range: getResultsRange(), count: getLocalizedFormattedNumber(count, locale) })}</h4>
                  </div>
                  {results.map((bookmark) => <RenderBookmarkList bookmarkData={bookmark} />)}
                </div>
                <ReactPagination
                  locale={locale}
                  forcePage={currentPage - 1}
                  pageCount={numPages}
                  onPageChange={({ selected }) => dispatch(fetchBookmarkTabData(courseIdFromUrl, selected + 1))}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

BookmarkTab.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(BookmarkTab);
